/**
 * Font Loader Utility for Omnivaso Platform
 * Supports Google Fonts, External Web Stylesheets (Adobe Fonts, Fontshare, etc.), 
 * and Uploaded/Direct Font Files (.woff2, .woff, .ttf, .otf) with @font-face rules.
 */

// Helper to determine format string from font file URL
export const getFontFormatFromUrl = (url = '') => {
    const cleanUrl = url.split('?')[0].toLowerCase();
    if (cleanUrl.endsWith('.woff2')) return 'woff2';
    if (cleanUrl.endsWith('.woff')) return 'woff';
    if (cleanUrl.endsWith('.ttf')) return 'truetype';
    if (cleanUrl.endsWith('.otf')) return 'opentype';
    return 'woff2';
};

// Generate CSS font-family property value for a custom font
export const getCustomFontCssFamily = (font) => {
    if (!font) return 'sans-serif';
    if (font.value && font.value.includes(',')) return font.value;
    
    const name = (font.name || font.label || '').trim().replace(/['"]/g, '');
    if (!name) return 'sans-serif';

    const category = (font.category || 'Sans-Serif').toLowerCase();
    let fallback = 'sans-serif';
    if (category.includes('serif')) fallback = 'serif';
    else if (category.includes('mono')) fallback = 'monospace';
    else if (category.includes('script') || category.includes('hand')) fallback = 'cursive';
    else if (category.includes('display')) fallback = 'sans-serif';

    return `'${name}', ${fallback}`;
};

// System fonts that do not need to be loaded from Google Fonts
export const SYSTEM_FONTS = new Set([
    'arial', 'times new roman', 'georgia', 'courier new', 'inherit',
    'initial', 'unset', 'system-ui', '-apple-system', 'sans-serif',
    'serif', 'monospace', 'cursive', 'fantasy', 'blinkmacsystemfont'
]);

// Extract primary family name from CSS font-family string (e.g. "'Playfair Display', serif" => "Playfair Display")
export const extractPrimaryFontName = (fontFamilyStr = '') => {
    if (!fontFamilyStr || typeof fontFamilyStr !== 'string') return '';
    const first = fontFamilyStr.split(',')[0].trim().replace(/['"]/g, '');
    return first;
};

// Check if a given font family is a standard system font
export const isSystemFont = (fontNameOrFamily = '') => {
    const name = extractPrimaryFontName(fontNameOrFamily).toLowerCase();
    return !name || SYSTEM_FONTS.has(name);
};

// Comprehensive curated Google Fonts suite matching config/fontOptions.js
export const CURATED_GOOGLE_FONTS_URL =
    'https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&family=Outfit:wght@400;600;700&family=Montserrat:wght@400;600;700&family=Roboto:wght@400;500;700&family=Poppins:wght@400;600;700&family=Plus+Jakarta+Sans:wght@400;600;700&family=Space+Grotesk:wght@400;600;700&family=DM+Sans:wght@400;500;700&family=Urbanist:wght@400;600;700&family=Work+Sans:wght@400;600&family=Sora:wght@400;600&family=Manrope:wght@400;600;700&family=Oswald:wght@400;600&family=Syne:wght@400;700;800&family=Syncopate:wght@400;700&family=Bebas+Neue&family=Anton&family=Unbounded:wght@400;700&family=Playfair+Display:ital,wght@0,400;0,700;1,400&family=Lora:ital,wght@0,400;0,700;1,400&family=Merriweather:wght@400;700&family=Cinzel:wght@400;700&family=Cinzel+Decorative:wght@400;700&family=Cormorant+Garamond:wght@400;600;700&family=Bodoni+Moda:wght@400;700&family=Prata&family=Fraunces:wght@400;700&family=Dancing+Script:wght@400;700&family=Pacifico&family=Caveat:wght@400;700&family=Sacramento&family=Abril+Fatface&family=Roboto+Mono:wght@400;600&family=Space+Mono:wght@400;700&family=Fira+Code:wght@400;600&family=JetBrains+Mono:wght@400;600&display=swap';

// Build Google Fonts CSS URL for a given font name
export const buildGoogleFontUrl = (fontName, weights = 'wght@300;400;500;600;700;800;900') => {
    const cleanName = (fontName || '').trim().replace(/['"]/g, '');
    if (!cleanName) return '';
    const encodedName = cleanName.split(' ').map(part => encodeURIComponent(part)).join('+');
    return weights 
        ? `https://fonts.googleapis.com/css2?family=${encodedName}:${weights}&display=swap`
        : `https://fonts.googleapis.com/css2?family=${encodedName}&display=swap`;
};

// Injects the comprehensive curated Google Fonts stylesheet into document.head
export const loadAllCuratedGoogleFonts = () => {
    if (typeof document === 'undefined') return;
    const existing = document.querySelector('link[data-omnivaso-curated-fonts="true"]');
    if (!existing) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = CURATED_GOOGLE_FONTS_URL;
        link.setAttribute('data-omnivaso-curated-fonts', 'true');
        document.head.appendChild(link);
    }
};

// Loads a single Google Font dynamically into document.head if not already present
export const loadGoogleFont = (fontNameOrValue = '') => {
    if (typeof document === 'undefined') return;
    const fontName = extractPrimaryFontName(fontNameOrValue);
    if (!fontName || isSystemFont(fontName)) return;

    // Check if already loaded by name
    const safeId = fontName.toLowerCase().replace(/[^a-z0-9]/g, '_');
    if (document.querySelector(`link[data-google-font="${safeId}"]`)) return;

    const url = buildGoogleFontUrl(fontName);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = url;
    link.setAttribute('data-google-font', safeId);
    document.head.appendChild(link);
};

// Ensures all fonts configured in settings & typography are actively loaded in document.head
export const ensureSettingsFontsLoaded = (settings = {}, config = {}) => {
    if (typeof document === 'undefined') return;
    const fonts = [
        settings?.brandFontFamily,
        settings?.navFontFamily,
        settings?.headerSubtitleFontFamily,
        settings?.currencySelectorFontFamily,
        settings?.fontFamily,
        settings?.headingFontFamily,
        settings?.heroTitleFontFamily,
        settings?.heroSubtitleFontFamily,
        settings?.buttonFontFamily,
        settings?.typography?.fontFamily,
        config?.typography?.fontFamily,
        config?.typography?.h1?.fontFamily,
        config?.typography?.h2?.fontFamily,
        config?.typography?.body?.fontFamily
    ];

    fonts.forEach(f => {
        if (f) loadGoogleFont(f);
    });
};

/**
 * Injects or updates custom font stylesheets and @font-face rules in document.head
 * @param {Array} customFonts - Array of custom font definitions
 */
export const loadCustomFonts = (customFonts = []) => {
    if (typeof document === 'undefined') return;

    if (!Array.isArray(customFonts)) {
        return;
    }

    const activeIds = new Set();

    customFonts.forEach((font, index) => {
        if (!font) return;
        const id = font.id || `cf-${index}-${(font.name || '').replace(/[^a-zA-Z0-9]/g, '_')}`;
        const fontName = (font.name || font.label || '').trim().replace(/['"]/g, '');
        if (!fontName) return;

        activeIds.add(id);

        const sourceType = font.sourceType || (font.fileUrl ? 'file' : (font.url && font.url.startsWith('http') && !font.url.includes('google') ? 'url' : 'google'));

        if (sourceType === 'google') {
            const fontUrl = font.url && font.url.startsWith('http') ? font.url : buildGoogleFontUrl(fontName);
            let linkEl = document.querySelector(`link[data-custom-font="${id}"]`);
            if (!linkEl) {
                linkEl = document.createElement('link');
                linkEl.rel = 'stylesheet';
                linkEl.setAttribute('data-custom-font', id);
                document.head.appendChild(linkEl);
            }
            if (linkEl.href !== fontUrl) {
                linkEl.href = fontUrl;
            }
        } else if (sourceType === 'url') {
            const stylesheetUrl = font.url || '';
            if (stylesheetUrl) {
                let linkEl = document.querySelector(`link[data-custom-font="${id}"]`);
                if (!linkEl) {
                    linkEl = document.createElement('link');
                    linkEl.rel = 'stylesheet';
                    linkEl.setAttribute('data-custom-font', id);
                    document.head.appendChild(linkEl);
                }
                if (linkEl.href !== stylesheetUrl) {
                    linkEl.href = stylesheetUrl;
                }
            }
        } else if (sourceType === 'file') {
            const fileUrl = font.fileUrl || font.url || '';
            if (fileUrl) {
                const format = font.format || getFontFormatFromUrl(fileUrl);
                const weight = font.weight || font.fontWeight || 'normal';
                const style = font.style || font.fontStyle || 'normal';
                const fontFaceRule = `
@font-face {
    font-family: '${fontName}';
    src: url('${fileUrl}') format('${format}');
    font-weight: ${weight};
    font-style: ${style};
    font-display: swap;
}
`;
                let styleEl = document.querySelector(`style[data-custom-font="${id}"]`);
                if (!styleEl) {
                    styleEl = document.createElement('style');
                    styleEl.setAttribute('data-custom-font', id);
                    document.head.appendChild(styleEl);
                }
                if (styleEl.textContent !== fontFaceRule) {
                    styleEl.textContent = fontFaceRule;
                }
            }
        }
    });

    // Cleanup removed fonts from DOM
    const existingFontEls = document.querySelectorAll('[data-custom-font]');
    existingFontEls.forEach(el => {
        const elId = el.getAttribute('data-custom-font');
        if (elId && !activeIds.has(elId)) {
            el.remove();
        }
    });
};
